import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import IconButton from 'components/IconButton';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import PencilIcon from '@mui/icons-material/EditOutlined';
import { styled } from '@mui/material/styles';
import Input from 'components/form/TextInput';
import DatePicker from 'components/form/DatePicker';
import Select from 'components/form/Select';
import toast from 'utils/toast';

const Root = styled(Box)`
  display: flex;
  align-items: center;
  .form-control {
    border: none;
    padding: 0;
    border-bottom: 1px dotted #999;
    height: auto;
    border-radius: 0;
    background: transparent;
    font-size: inherit;
  }
`;

const TextInlineEdit = ({
  type,
  disabled,
  editing,
  text,
  label,
  placeholder,
  normalize,
  onSave,
  onCancel,
  className,
  size,
  options,
  style,
  required,
  fieldName,
  editOnHover,
  sx,
}) => {
  const [value, setValue] = useState();
  const [isEditing, setIsEditing] = useState(editing);
  const [hover, setHover] = useState(!editOnHover);

  useEffect(() => {
    setIsEditing(editing);
  }, [editing]);

  useEffect(() => {
    setValue(text);
  }, [text]);

  const onCancelEdit = () => {
    setValue(text);
    if (onCancel) {
      onCancel();
    } else {
      setIsEditing(false);
    }
  };

  const onTextChange = (e) => {
    const { value } = e.target;
    if (normalize) {
      setValue(normalize(value));
    } else {
      setValue(value);
    }
  };

  const onDateChange = (date) => {
    setValue(date);
  };

  const onSelect = (v) => {
    setValue(v);
  };

  const cancelEdit = () => {
    setValue(text);
    onCancel();
  };

  const handleSave = () => {
    if (required && !value) {
      toast.error(`${fieldName} cannot be empty`);
    } else {
      const result = onSave(value);
      if (result && result.then) {
        return result.then(() => {
          setIsEditing(false);
        });
      } else if (editing === undefined) {
        setIsEditing(false);
      }
    }
  };

  if (isEditing) {
    let component;
    if (type === 'date') {
      component = (
        <DatePicker
          margin="none"
          size={size}
          value={value}
          placeholder={placeholder}
          onChange={onDateChange}
        />
      );
    } else if (type === 'select') {
      component = (
        <Select
          fullWidth
          transformValue
          options={options}
          value={value}
          onChange={onSelect}
          size="small"
        />
      );
    } else {
      component = (
        <Input
          fullWidth
          inputProps={{
            autoFocus: true,
          }}
          type={type}
          size={size}
          margin="none"
          placeholder={placeholder}
          value={value}
          onChange={onTextChange}
        />
      );
    }
    return (
      <Root className={className} style={style}>
        {component}
        <Box ml={1} display="flex" alignItems="center">
          <IconButton
            disabled={!value && required}
            color="primary"
            icon={<CheckIcon />}
            onClick={handleSave}
          />
          <IconButton
            icon={<ClearIcon />}
            color="error"
            onClick={onCancelEdit}
          />
        </Box>
      </Root>
    );
  } else {
    const showEdit = editOnHover ? hover : true;

    return (
      <Stack
        sx={sx}
        spacing={1}
        direction="row"
        alignItems="center"
        onMouseEnter={setHover}
        onMouseLeave={() => setHover()}
      >
        <span>{label || placeholder}</span>
        {editing === undefined && !isEditing && !disabled && (
          <IconButton
            size="small"
            sx={{ opacity: showEdit ? 1 : 0, transition: 'opacity 0.2s' }}
            icon={<PencilIcon sx={{ color: 'action', fontSize: 'inherit' }} />}
            onClick={() => setIsEditing(true)}
          />
        )}
      </Stack>
    );
  }
};

export default TextInlineEdit;
